import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { PageHeader } from "@/components/site/primitives";
import { legalIndex, legalDoc } from "@/lib/api";

export default function Legal() {
  const [params] = useSearchParams();
  const [docs, setDocs] = useState([]);
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    legalIndex()
      .then((d) => setDocs(d || []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, []);

  // Deep-link support: /legal?doc=privacy_policy | terms_of_service (from footer links)
  useEffect(() => {
    const doc = params.get("doc");
    if (doc) open(doc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function open(type) {
    setActive({ loading: true });
    try {
      const doc = await legalDoc(type);
      setActive(doc);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch {
      setActive({ error: true });
    }
  }

  return (
    <div data-testid="legal-page">
      <PageHeader
        label="LEGAL"
        title={"Agreements &\nnotices."}
        intro="The current published versions of AIROTECH's public legal documents. Client-specific agreements (NDA, Development Agreement / SOW, IP Assignment) are presented securely within your workspace."
      />

      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-[320px_1fr] gap-10 md:gap-16">
          <nav className="space-y-2" data-testid="legal-doc-list">
            {loading && <p className="text-sm text-neutral-500">Loading…</p>}
            {!loading && docs.length === 0 && (
              <p className="text-sm text-neutral-500" data-testid="legal-empty">
                Documents will appear here once published.
              </p>
            )}
            {docs.map((d) => (
              <button
                key={d.document_type}
                data-testid={`legal-doc-${d.document_type}`}
                onClick={() => open(d.document_type)}
                className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
                  active && active.document_type === d.document_type
                    ? "border-cyan-400/60 bg-cyan-400/10 text-white"
                    : "border-[#1f1f1f] text-zinc-300 hover:border-cyan-400/40"
                }`}
              >
                <span className="block text-sm font-medium">{d.title}</span>
                <span className="block text-xs opacity-60 mt-0.5">
                  v{d.version}
                  {d.effective_date ? ` · Effective ${d.effective_date}` : ""}
                </span>
              </button>
            ))}
          </nav>

          <div>
            {!active && (
              <p className="text-sm text-neutral-500" data-testid="legal-hint">
                Select a document to read it.
              </p>
            )}
            {active && active.loading && (
              <p className="text-sm text-neutral-500">Loading document…</p>
            )}
            {active && active.error && (
              <p className="text-sm text-red-600" data-testid="legal-error">
                This document could not be loaded.
              </p>
            )}
            {active && active.content && (
              <motion.article
                key={active.document_type}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                data-testid="legal-doc-content"
              >
                <header className="mb-6 pb-6 border-b border-[#232323]">
                  <h2 className="text-2xl font-semibold text-white">{active.title}</h2>
                  <p className="text-xs text-zinc-500 mt-1">
                    Version {active.version}
                    {active.effective_date ? ` · Effective ${active.effective_date}` : ""}
                  </p>
                </header>
                <div className="whitespace-pre-wrap text-sm leading-relaxed text-zinc-300">
                  {active.content}
                </div>
              </motion.article>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
